import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';

import { JustifiedGallery } from '../../app/Layouts/JustifiedGallery/JustifiedGallery';
import {
  CONDENSED_JUSTIFIED_GALLERY_HEIGHTS,
  VERTICAL_JUSTIFIED_GALLERY_HEIGHTS,
} from '../../app/Layouts/JustifiedGallery/constants';
import { JustifiedGalleryProps } from '../../app/Layouts/JustifiedGallery/types';
import VideoStockItemCard from '../../app/Search/components/card/VideoStockItemCard';
import { selectSearchLogParameters } from '../../app/Search/selectors/searchSelectors';
import fetchSimilarItems from '../../app/Search/utils/fetchSimilarItems';
import { ContentClass } from '../../app/Shared/enums';
import LoadingIndicator from '../../common/components/LoadingIndicator';
import { areAllItemsVertical } from '../../common/stockItems/utils';
import { StockItemContext } from '../../common/types/StockItemTypes';
import { selectRedirectToSignUpLink } from '../selectors/detailsSelectors';
import SimilarClipsHeader from './SimilarClipsHeader';

interface JustifiedGallerySimilarStockItemsProps {
  detailsUrl: string;
  contentClass: ContentClass;
  contentType?: string;
  headerClassName?: string;
  originArea: OriginArea;
  justifiedGalleryProps?: Partial<JustifiedGalleryProps>;
}
// NOTE: This can likely replace FullWidthSimilarStockItems if Image search is ever refactored to use JustifiedGallery.

export const JustifiedGallerySimilarStockItems: React.FC<
  JustifiedGallerySimilarStockItemsProps
> = ({
  detailsUrl,
  contentClass,
  contentType,
  headerClassName = '',
  originArea,
  justifiedGalleryProps = {},
}) => {
  const [similarStockItems, setSimilarStockItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const redirectToSignUpLink = useSelector(selectRedirectToSignUpLink);
  const searchLogParameters = useSelector(selectSearchLogParameters);

  // Delays searching for similar stock items until component is rendered
  useEffect(() => {
    const fetchSimilarStockItems = async () => {
      try {
        const searchResult = await fetchSimilarItems(detailsUrl, contentClass);
        setSimilarStockItems(searchResult);
      } catch (error) {
        console.error('Error fetching similar stock items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSimilarStockItems();
  }, []);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!similarStockItems || similarStockItems.length < 1) return null;

  // Check if all items are vertical
  const allItemsAreVertical = areAllItemsVertical(similarStockItems);
  const responsiveHeights = allItemsAreVertical
    ? VERTICAL_JUSTIFIED_GALLERY_HEIGHTS
    : CONDENSED_JUSTIFIED_GALLERY_HEIGHTS;

  return (
    <SimilarClipsHeader
      contentType={contentType}
      className={twMerge('mb-5', headerClassName)}
    >
      <JustifiedGallery
        items={similarStockItems.map((item) => ({
          id: item.stockItem?.id,
          aspectRatio: item.stockItem?.aspectRatio,
        }))}
        className="stock-item-group-wrapper card-design"
        responsiveHeights={responsiveHeights}
        {...justifiedGalleryProps}
      >
        {({ index }) => {
          const stockItemDetails = similarStockItems[index];
          const { stockItem } = stockItemDetails;
          return (
            <VideoStockItemCard
              key={stockItem.id}
              page={null}
              redirectToSignUpLink={redirectToSignUpLink}
              searchLogParameters={searchLogParameters}
              hideOnThreeColumnLayout={false}
              stockItem={stockItem}
              stockItemFormats={stockItemDetails.stockItemFormats}
              context={StockItemContext.SIMILAR_CONTENT}
              clickOriginArea={originArea}
              shouldStockItemHaveNewFlag={stockItem.shouldStockItemHaveNewFlag}
              isSelected={false}
              shouldShowDownloadButton
              thumbnailClassNames="rounded-none md:rounded-2xl"
              overlayGradientClassNames="rounded-none md:rounded-2xl"
              videoStyles={{ width: '100%', height: '100%' }}
            />
          );
        }}
      </JustifiedGallery>
    </SimilarClipsHeader>
  );
};
